import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { App } from '@capacitor/app';
import { AlertController } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import { AppConfigService } from '../../../common/config/app.config.service';
import { ApiResponseDto } from '../../../common/dtos/api-response.dto';
import { HttpCustomHeader } from '../../../common/enums/http-custom-header.enum';
import { suppressErrorToastContext } from '../../../common/http/suppress-error-toast.context';
import { AppHttpHooks } from '../../../common/interceptors/app-http-hooks';
import { LogFactory } from '../../../common/services/logger/log-factory';
import { PlatformService } from '../../../common/services/platform/platform.service';
import { AppRefreshable, RefreshPolicy } from '../visibility/app-refresh';
import { RefreshKey } from '../visibility/refresh-key.enum';

// How long a version policy answer holds before a resume re-asks. The 426
// backstop still blocks a newly unsupported build on its next request.
const VERSION_POLICY_STALE_AFTER_MS = 10 * 60 * 1000;

// Prompt copy. Native sends the user to a store; web has no store to send
// anyone to, so it reloads the page it is already on, and the words have to say
// so - "Update" in a browser reads as something you go somewhere else to do.
const UPDATE_REQUIRED_HEADER = 'Update required';
const UPDATE_AVAILABLE_HEADER = 'Update available';
const STORE_ACTION = 'Update';
const RELOAD_ACTION = 'Reload';
const SNOOZE_ACTION = 'Later';
const REQUIRED_NATIVE_MESSAGE =
  'Please update to the latest version to continue.';
const REQUIRED_WEB_MESSAGE = 'Please reload to continue.';
const OPTIONAL_NATIVE_MESSAGE = 'A new version is available.';
const OPTIONAL_WEB_MESSAGE = 'A new version is available. Reload to get it.';

export type VersionState = 'NONE' | 'OPTIONAL' | 'REQUIRED';

/** Client platform the update policy is keyed by. Mirrors the API's enum. */
export type GatePlatform = 'ios' | 'android' | 'web';

export interface VersionCheckResponse {
  state: VersionState;
  minSupportedBuild: number;
  latestBuild: number;
  message: string | null;
  storeUrl: string | null;
  softDeadline: string | null;
}

/**
 * Server-driven app version gate (Item 2). On launch, and on a resume once the
 * last answer has aged, the app asks the API whether its build needs to update,
 * then does nothing (NONE), shows a snoozable prompt (OPTIONAL), or shows a
 * blocking, non-dismissible screen (REQUIRED). It also provides the platform +
 * build headers the auth interceptor stamps on every request, letting the API's
 * 426 backstop catch sub-minimum builds. Snooze/grace are server-driven; nothing
 * is hard-coded here. See ADR-0004.
 *
 * App-only, so it lives in core/ rather than common/: it reaches the shared
 * interceptor through APP_HTTP_HOOKS, and resume through APP_REFRESH as an
 * EAGER entry — the blocking answer must be right before the user acts
 * (ADR-0264).
 *
 * WEB IS GATED TOO (ADR-0281). It used to be excluded on the reasoning that a
 * browser always has the newest bundle, which is true of a browser and false of
 * a TAB: one left open across a deploy keeps calling the API with the build it
 * was loaded with, for as long as the user leaves it open. That is an old
 * install by another name, and since the API deploys ahead of the frontends, it
 * is the case the gate exists for. The only difference is the remedy - there is
 * no store to open, so web reloads the page instead.
 */
@Injectable({ providedIn: 'root' })
export class VersionGateService implements AppRefreshable, AppHttpHooks {
  readonly refreshKey = RefreshKey.VersionGate;
  readonly staleAfterMs = VERSION_POLICY_STALE_AFTER_MS;
  readonly refreshPolicy = RefreshPolicy.Eager;

  private readonly http = inject(HttpClient);
  private readonly config = inject(AppConfigService);
  private readonly platformService = inject(PlatformService);
  private readonly alertController = inject(AlertController);
  private readonly logger = inject(LogFactory).createLogger(VersionGateService);

  private build: number | null = null;
  private platform: GatePlatform | null = null;
  /** In-flight/settled {@link readBuild}, so it happens exactly once. */
  private buildLoaded: Promise<void> | null = null;
  private optionalSnoozedThisSession = false;
  // Once the blocking REQUIRED alert is up it never dismisses, so suppress
  // duplicates — a sub-minimum build can fire many parallel 426s at once.
  private requiredAlertVisible = false;

  /**
   * Learn which platform this is and which build it is running - nothing more.
   *
   * Run as an app initializer (app.config.ts), because until it has finished
   * {@link requestHeaders} has nothing to stamp, and every request made in the
   * meantime reaches the API anonymous as to its build: not gateable by the 426
   * backstop, and invisible to any compatibility branch the API takes
   * (ADR-0281). Boot is exactly when the app makes its first calls, so "a few
   * requests at startup" was most of a cold launch. It touches no network of its
   * own and cannot reject, so blocking the bootstrap on it costs a Capacitor
   * bridge round-trip.
   *
   * Memoised: the app initializer calls this, and so does {@link initialize}
   * from AppComponent a moment later. Both want it done, neither wants to be
   * the one that has to know the other ran.
   */
  async loadBuild(): Promise<void> {
    this.buildLoaded ??= this.readBuild();
    return this.buildLoaded;
  }

  private async readBuild(): Promise<void> {
    if (this.platformService.isWeb) {
      // The bundle carries its own number (stamped from environments/version.json
      // at build time); there is no container to ask.
      this.platform = 'web';
      this.build = this.config.appBuild;
      if (this.build === null) {
        this.logger.warn(
          'Web bundle has no build number; version gating is inert here.',
        );
      }
      return;
    }

    this.platform = this.platformService.isIos() ? 'ios' : 'android';
    try {
      const info = await App.getInfo();
      // `build` is a string ("142"); compare on monotonic integers. A build
      // that will not parse is left null rather than stamped as "NaN", which
      // the API would discard anyway - and a header that is present but
      // meaningless is harder to read in a log than one that is absent.
      const build = Number(info.build);
      this.build = Number.isFinite(build) ? build : null;
    } catch (err) {
      this.logger.warn('Could not read app build number for version gate.', err);
    }
  }

  /**
   * Load the build number. Call at app startup, then run the launch check
   * through AppRefreshCoordinator.forceRefresh.
   */
  async initialize(): Promise<void> {
    await this.loadBuild();
  }

  /** Headers for the auth interceptor (null before the build is known). */
  requestHeaders(): Record<string, string> | null {
    if (this.platform === null || this.build === null) return null;
    return {
      [HttpCustomHeader.APP_PLATFORM]: this.platform,
      [HttpCustomHeader.APP_BUILD]: String(this.build),
    };
  }

  /** The auth interceptor saw a 426 Upgrade Required. */
  onUpgradeRequired(message: string | null): void {
    void this.notifyUpgradeRequired(message);
  }

  /**
   * Check, then present the prompt for the returned state. Rejects when the
   * check could not complete, so the coordinator retries on the next resume
   * instead of treating a failure as a fresh answer. A no-op while no platform
   * has been resolved.
   */
  async refreshNow(): Promise<void> {
    if (this.platform === null) return;
    const result = await this.check();
    if (!result) throw new Error('Version check did not complete');
    if (result.state === 'REQUIRED') {
      await this.presentRequired(result);
    } else if (result.state === 'OPTIONAL' && !this.optionalSnoozedThisSession) {
      await this.presentOptional(result);
    }
  }

  /** Hit the version-check endpoint; returns null when un-gated or on error. */
  async check(): Promise<VersionCheckResponse | null> {
    if (this.platform === null || this.build === null) return null;
    const params = new HttpParams()
      .set('platform', this.platform)
      .set('build', String(this.build));
    try {
      const res = await firstValueFrom(
        this.http.get<ApiResponseDto<VersionCheckResponse>>(
          `${this.config.apiEndpoint}/app/version-check`,
          // Runs on launch and resume and fails open — no toast (ADR-0263).
          { params, context: suppressErrorToastContext() },
        ),
      );
      return res.data ?? null;
    } catch (err) {
      this.logger.warn('Version check failed; failing open.', err);
      return null;
    }
  }

  /**
   * Honour an HTTP 426 from the API's MinVersionInterceptor (the backstop). The
   * auth interceptor calls this when any request is rejected for a sub-minimum
   * build, so the blocking screen appears even if the policy tightened
   * mid-session (no wait for the next launch/resume). Re-checks the policy for
   * the store URL/message; falls back to the 426 payload's message if the
   * version-check itself can't be reached.
   */
  async notifyUpgradeRequired(fallbackMessage?: string | null): Promise<void> {
    if (this.platform === null) return; // nothing was stamped, nothing to gate
    const result = await this.check();
    if (result && result.state === 'REQUIRED') {
      await this.presentRequired(result);
      return;
    }
    // check() failed open (or disagreed) — still block using the 426's message
    // and whatever store URL we last learned.
    await this.presentRequired({
      state: 'REQUIRED',
      minSupportedBuild: 0,
      latestBuild: 0,
      message: fallbackMessage ?? null,
      storeUrl: result?.storeUrl ?? null,
      softDeadline: null,
    });
  }

  private async presentRequired(r: VersionCheckResponse): Promise<void> {
    if (this.requiredAlertVisible) return; // don't stack blocking alerts
    this.requiredAlertVisible = true;
    const onWeb = this.platform === 'web';
    const alert = await this.alertController.create({
      header: UPDATE_REQUIRED_HEADER,
      message:
        r.message ?? (onWeb ? REQUIRED_WEB_MESSAGE : REQUIRED_NATIVE_MESSAGE),
      backdropDismiss: false,
      buttons: [
        {
          text: onWeb ? RELOAD_ACTION : STORE_ACTION,
          handler: () => {
            this.applyUpdate(r.storeUrl);
            return false; // keep the blocking alert open
          },
        },
      ],
    });
    // Should the blocking alert ever be dismissed by a path not foreseen here,
    // re-arm so the next check/426 presents it again instead of leaving the
    // app silently un-gated until relaunch.
    void alert.onDidDismiss().then(() => (this.requiredAlertVisible = false));
    await alert.present();
  }

  private async presentOptional(r: VersionCheckResponse): Promise<void> {
    const onWeb = this.platform === 'web';
    const alert = await this.alertController.create({
      header: UPDATE_AVAILABLE_HEADER,
      message:
        r.message ?? (onWeb ? OPTIONAL_WEB_MESSAGE : OPTIONAL_NATIVE_MESSAGE),
      // A backdrop tap (or Android hardware back, which Ionic routes through
      // the same check) must not skip the choice — Later is the only snooze.
      backdropDismiss: false,
      buttons: [
        {
          text: SNOOZE_ACTION,
          role: 'cancel',
          handler: () => {
            this.optionalSnoozedThisSession = true;
          },
        },
        {
          text: onWeb ? RELOAD_ACTION : STORE_ACTION,
          handler: () => this.applyUpdate(r.storeUrl),
        },
      ],
    });
    await alert.present();
  }

  /**
   * Do whatever "get the new version" means on this platform: open the store
   * listing on native, reload the page on web.
   *
   * A web reload is not a poor substitute for the store trip - it IS the
   * update. The newest bundle is already sitting on the server; the only thing
   * standing between the user and it is the tab they are looking at.
   */
  private applyUpdate(storeUrl: string | null): void {
    if (this.platform === 'web') {
      // Through PlatformService rather than the global: it is the seam that
      // already owns "is there a window, and which one", and it is the only
      // form of this that a spec can stand in for - jsdom will not let
      // window.location be redefined.
      this.platformService.getWindow()?.location.reload();
      return;
    }
    if (storeUrl) window.open(storeUrl, '_system');
  }
}
